import {useState, useEffect, useCallback, useRef} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../redux/store';
import {setIsFieldsChanged} from '../redux/slice/productionSlice';
import {
  ROUTE_PRODUCTION_EDIT,
  ROUTE_PRODUCTION_HOME,
  ROUTE_PRODUCTION_NEW,
} from '../components/productions/create-new-production/CreateNewProductionUtils';

// Custom hook to manage unsaved changes in production forms
export const useNavigationGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isFieldsChanged = useSelector(
    (state: RootState) => state.production.isFieldsChanged
  );

  const [nextPath, setNextPath] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const urlRef = useRef('');

  // Function to intercept in-app navigation
  const blockNavigation = useCallback(
    (path: string) => {
      if (isFieldsChanged && path !== location.pathname) {
        setNextPath(path);
        setModalVisible(true);
        return false;
      }
      return true;
    },
    [isFieldsChanged, location.pathname]
  );

  const confirmNavigation = () => {
    dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    setModalVisible(false);

    if (nextPath && nextPath !== location.pathname) {
      const pathToNavigate = nextPath;
      setNextPath(null);

      // Delay navigation slightly to let modal close
      setTimeout(() => {
        navigate(pathToNavigate);
      }, 300); // match modal animation duration
    }
  };

  const cancelNavigation = () => {
    setModalVisible(false);
    setNextPath(null);
  };

  // Warn on browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFieldsChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFieldsChanged]);

  useEffect(() => {
    // checking for production stepper pages and isFieldsChanged to detect changes
    const isProductionPath =
      location.pathname == ROUTE_PRODUCTION_EDIT || ROUTE_PRODUCTION_NEW;

    if (!isFieldsChanged || !isProductionPath) return;

    const fullUrl = location.pathname + location.search + location.hash;

    // store latest URL
    urlRef.current = fullUrl;

    // push initial state
    window.history.pushState(null, '', fullUrl);

    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();

      // stop navigation
      window.history.pushState(null, '', urlRef.current);

      // show confirmation modal
      setModalVisible(true);

      // save intended path
      setNextPath(ROUTE_PRODUCTION_HOME); // redirect to production home on confirm
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      setModalVisible(false);
    };
  }, [location.pathname, location.search, location.hash, isFieldsChanged]);

  return {blockNavigation, modalVisible, confirmNavigation, cancelNavigation};
};
